import { useMemo } from 'react';
import { _extractOptionsFromContentTypes } from './_extractOptionsFromContentTypes';
import { _extractAllAnnoBasedDimensionsAsOptions } from './_extractAllAnnoBasedDimensionsAsOptions';
import { useGetQueryData } from '@/utils/react-query-hooks/general';
import { useGroupedSubThemeOptions } from './useGroupedSubThemeOptions';
import { useTopicClusteringDimensionOptions } from './useTopicClusteringDimensionOptions';
import { segrigateDocs, isTplAnnotationEnabled, extractAllBlocksFromTpl } from '@okf/ootils';


// depending on what is selected for dimension 1, we need to figure what to show for dimension 2
 // if a standard field is selected (not anno):
// ---- then get only the standard fields from the same tpl THAT IS NOT the one selected in dim 1 + anno based dimensions
// if a rollup of standard field is selected (not anno)
// ---- then check on which all tpls this rolled up tpl has been used as a tag, show all those tpls standard options as well as rollup options THAT ARE NOT equal to the rollup options selected in dim 1 + anno based dimensions
// if a standard anno field is selected (not rollup anno) || if a rollup anno field is selected
// ---- then check all tpls that have anno enabled and get their standard fields + anno based dimensions THAT IS NOT the one selected in dim 1
/** Group flat standard-field options by contentType, matching useGetAllOptionsForDimension1 shape */
const groupByContentType = (flatOptions, allTpls) => {
  const grouped = segrigateDocs(flatOptions || [], 'contentType');
  return Object.keys(grouped).map(ct => ({
    display: allTpls.find(tpl => tpl.kp_content_type === ct)?.general?.content?.title || ct,
    options: grouped[ct]
  }));
};

export const getAllOptionsForDimension2 = ({dimension1Value, allTpls, isSelfManagedTenant, annotationTagsCount, smTagTypesConfig, groupedSubThemeOptions = [], topicClusteringOptions = []}) => {
  if (!dimension1Value || !allTpls) return [];

  const {
    annoRollupOptions,
    annoTagOptions
  } = _extractAllAnnoBasedDimensionsAsOptions({
    allTpls,
    isSelfManagedTenant,
    annotationTagsCount,
    smTagTypesConfig,
  });

  // groupedSubThemeOptions: same options, same ordering as dimension 1 —
  // they're what the cascade folds into the Thematics/Attributes folders.
  const annoGroup = (annoTagOptions.length > 0 || annoRollupOptions.length > 0 || groupedSubThemeOptions.length > 0 || topicClusteringOptions.length > 0)
    ? [{ display: 'Annotation Tags Based', options: [...annoTagOptions, ...groupedSubThemeOptions, ...topicClusteringOptions, ...annoRollupOptions] }]
    : [];

  // Check if dimension1 is an annotation-based field
  const isAnnoField = dimension1Value.isAnno

  if (isAnnoField) {
    // If annotation field is selected (standard or rollup anno)
    // Get all publishing-segment tpls with annotations enabled and their standard fields + anno dimensions
    const tplsWithAnnotationsEnabled = allTpls.filter(isTplAnnotationEnabled);

    const standardFieldsFromAnnoEnabledTpls = _extractOptionsFromContentTypes({
      contentTypes: tplsWithAnnotationsEnabled.map(tpl => tpl.kp_content_type),
      allTpls
    });

    return [
      ...annoGroup,
      ...groupByContentType(standardFieldsFromAnnoEnabledTpls, allTpls)
    ];
  }

  const isRollupField = dimension1Value.rollupType;

  if (!isRollupField) {
    // Standard field is selected (not anno, not rollup)
    // Get contentType and valuePath from dimension1Value properties
    const contentType = dimension1Value.contentType

    const sameTemplateOptions = _extractOptionsFromContentTypes({
      contentTypes: [contentType],
      allTpls
    }).filter(opt => opt.value !== dimension1Value.value); // Exclude the selected dimension1

    return [
      ...annoGroup,
      ...groupByContentType(sameTemplateOptions, allTpls)
    ];
  } else {
    // Rollup field is selected (not anno)
    let rolledUpContentType;

    if (dimension1Value.rollupType === 'tagType') {
      rolledUpContentType = dimension1Value.rollupPath[0];
    } else if (dimension1Value.rollupType === 'valuePathType') {
      rolledUpContentType = dimension1Value.tagTypeCollectionToRollup;
    }

    // Find all templates that use this content type as a tag
    const templatesUsingThisAsTag = allTpls.filter(tpl => {
      const allBlocks = extractAllBlocksFromTpl({ tpl });
      const tagBlocks = allBlocks.filter(bl => bl.valuePath.startsWith("tags."));
      return tagBlocks.some(tagBlock => tagBlock.props.tagType === rolledUpContentType);
    });

    // Get options from these templates (_extractOptionsFromContentTypes already includes rollup options)
    const optionsFromRelatedTpls = _extractOptionsFromContentTypes({
      contentTypes: templatesUsingThisAsTag.map(tpl => tpl.kp_content_type),
      allTpls
    }).filter(opt => opt.value !== dimension1Value.value); // Exclude the selected dimension1

    return [
      ...annoGroup,
      ...groupByContentType(optionsFromRelatedTpls, allTpls)
    ];
  }
};

/** True when `dimension2Value` is one of the dimension-2 options for `dimension1Value`. */
export const isValidDimension2For = ({ dimension2Value, ...optionsParams }) => {
  if (!dimension2Value) return false;
  const flat = getAllOptionsForDimension2(optionsParams)
    .flatMap((o) => (Array.isArray(o.options) ? o.options : [o]));
  return flat.some((o) => o.value === dimension2Value.value);
};

/**
 * Dimension-2 (and dimension-N) options for the value picked one slot before —
 * the hook counterpart of useGetAllOptionsForDimension1. Sources tpls/platform
 * configs and the grouped sub-theme options itself, so callers pass only the
 * previous slot's value. Pass null (e.g. slot not reachable yet) → [].
 */
export const useGetAllOptionsForDimension2 = ({ dimension1Value }) => {
  const allTpls = useGetQueryData('allTpls');
  const { SELF_MANAGED_BASE_CONFIGS, annotationTagsCount } = useGetQueryData('platformConfigs');
  const groupedSubThemeOptions = useGroupedSubThemeOptions();
  const topicClusteringOptions = useTopicClusteringDimensionOptions();

  return useMemo(() => getAllOptionsForDimension2({
    dimension1Value,
    allTpls,
    isSelfManagedTenant: SELF_MANAGED_BASE_CONFIGS.enable === true,
    annotationTagsCount,
    smTagTypesConfig: SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null,
    groupedSubThemeOptions,
    topicClusteringOptions,
  }), [dimension1Value, allTpls, SELF_MANAGED_BASE_CONFIGS, annotationTagsCount, groupedSubThemeOptions, topicClusteringOptions]);
};
